import { debounce } from 'lodash-es';
import { companiesService } from '~/services/companies-service';
import { exposuresService } from '~/services/exposures-service';
import type { PublicCompany } from '~/types/person/companies';
import {
    getExposureStatusLabel,
    type CompanyExposure,
    type Exposure,
    type ExposureStatus,
    type FiguresNoteState,
    type PersonExposure,
    type RemovalActivity,
} from '~/types/person/details';
import {
    OTHER_COMPANIES_LIMIT,
    OTHER_COMPANIES_LOOKUP_DELAY_MS,
    OTHER_COMPANIES_MAX_PAGES,
    OTHER_COMPANIES_MIN_QUERY_LENGTH,
    OTHER_COMPANIES_PAGE_SIZE,
    selectOtherCompanies,
    shouldShowOtherCompanies,
} from './otherCompanyResults';

// How many companies one page of the Details list holds. Data Brokers pages the same list by 10 (BrokersTable).
const DETAILS_PAGE_SIZE = 8;

// Why the list is empty: the read found no companies, a filter narrowed them all away, or a search matched none.
export type ExposureEmptyCause = 'nothing-found' | 'filters' | 'search';

// 'priority' is the default ordering; the sortable table columns are everything else.
export type ExposureSortKey = 'company' | 'lastAction' | 'events' | 'active' | 'inProgress' | 'closed' | 'status' | 'priority';
export type ExposureSortColumn = Exclude<ExposureSortKey, 'priority'>;

export const useExposuresStore = defineStore('exposures', () => {
    const personal = usePersonStore();

    // Bumped whenever a details fetch starts; only the newest generation may write state. A person switch clears the
    // held details without bumping it, because each fetch also checks it is still for the person on screen.
    let detailsGeneration = 0;
    // The newest details fetch still waiting on its answer.
    const pendingDetailsFetch = ref<{ generation: number; personId: number } | null>(null);
    // In flight only while that fetch is for the person on screen: a switch disowns it without anyone settling it.
    const isFetching = computed(
        () => pendingDetailsFetch.value !== null && pendingDetailsFetch.value.personId === personal.person?.id
    );
    // The last details read's failure to bring its rows. A read in flight keeps it, so a retry leaves the failure on
    // screen, and the control that retries it, rather than swapping them for a loading state. A read that succeeds
    // clears it, as does a person switch.
    const fetchError = ref<string | null>(null);
    // Whether this visit's read has settled. A visit starts by resetting it (startVisit), and a person switch resets it
    // too.
    const hasLoaded = ref(false);
    const isResetting = ref(false);

    const details = ref<CompanyExposure[]>([]);
    // The member's all-time removal figures. They arrive in the same answer as `details`, tallied by the server over
    // the same read of the member's exposures, so the two never come from different reads.
    const removalActivity = ref<RemovalActivity | null>(null);
    // The last details read's failure to bring the removal figures: a failed read, or an answer whose rows came without
    // them. Held figures stay beside it, and it is what says they may be out of date. Like `fetchError`, a read in
    // flight keeps it, a read that brings the figures clears it, and so does a person switch.
    const removalActivityError = ref<string | null>(null);
    // When the rows, and separately the figures, held for the person on screen arrived (epoch ms), or null while none
    // has. A held answer is an answer even when it holds no rows, and what a failed read leaves on screen is dated by
    // these. The figures keep their own time because an answer can bring rows without them, which leaves the figures
    // beside them as old as the read that did bring them.
    const detailsUpdatedAt = ref<number | null>(null);
    const removalActivityUpdatedAt = ref<number | null>(null);

    // This visit's read failed and no answer for this person is held to show instead. An answer with no rows is an
    // answer, so a failed read after one leaves its empty state, and the counts it gives, on screen, dated by
    // `detailsStaleSince`, rather than calling them unavailable.
    const loadFailed = computed(() => hasLoaded.value && fetchError.value !== null && detailsUpdatedAt.value === null);

    // When the rows on screen arrived, while a read since has failed to bring them again; null while they are current,
    // or while none are held. The held list, every figure a page counts from it and a held empty state are that old.
    const detailsStaleSince = computed(() => (fetchError.value !== null ? detailsUpdatedAt.value : null));

    // What is said beside the removal figures: the one decision for every surface that states them, the dashboard card
    // and both exposures pages, so they say the same thing about the same figures. Figures held for this person that
    // the last read did not bring again may be out of date, and are as old as the read that brought them, which can be
    // older than the rows beside them. With none held there is nothing of theirs to date: they did not come, whether the
    // read that should have brought them failed or answered without them, and that is said once this visit's read has
    // settled, so a failure an earlier visit left is not said while this one's read is out.
    const figuresNote = computed<FiguresNoteState | null>(() => {
        if (removalActivity.value) {
            return removalActivityError.value ? { unavailable: false, staleSince: removalActivityUpdatedAt.value } : null;
        }
        return removalActivityError.value && hasLoaded.value ? { unavailable: true, staleSince: null } : null;
    });

    const exposures = ref<PersonExposure[]>([]);
    const exposuresError = ref<string | null>(null);

    // A company's exposure list, keyed by company id. Four surfaces expand or open a company —
    // the details table, its mobile cards, the broker table's drawer — and each one asked the API
    // for the same list, one of them twice inside a single component.
    const exposuresByCompany = ref<Record<number, Exposure[]>>({});
    const loadingCompanyIds = ref<Record<number, boolean>>({});
    const companyErrors = ref<Record<number, string>>({});
    const inFlightByCompany = new Map<number, Promise<Exposure[]>>();

    //pagination
    const currentPage = ref(1);
    const searchQuery = ref('');
    const selectedType = ref('');
    const selectedStatus = ref('');

    // Sorting
    const sortKey = ref<ExposureSortKey>('priority');
    const sortOrder = ref<'asc' | 'desc'>('desc');

    const expandedCompanyName = ref<string | null>(null);

    // External search state
    const externalSearchResults = ref<PublicCompany[]>([]);
    const isSearchingExternal = ref(false);
    const externalSearchError = ref<string | null>(null);
    // Bumped whenever a search starts or is abandoned; only the newest generation may write state.
    let externalSearchGeneration = 0;

    const debouncedExternalSearch = debounce(async (query: string) => {
        if (query && query.length >= OTHER_COMPANIES_MIN_QUERY_LENGTH) {
            await searchExternalCompanies(query);
        }
    }, OTHER_COMPANIES_LOOKUP_DELAY_MS);

    // A lookup belongs to the page that scheduled it. usePbApi takes the session of the stores
    // active when a request leaves, so once this store's scope ends (its pinia disposed with its
    // page), nothing it scheduled is sent and a walk in flight requests no further page.
    onScopeDispose(() => {
        debouncedExternalSearch.cancel();
        invalidateExternalSearch();
    });

    // The held companies the search and filters leave, in the chosen order. A copy, because the sort below orders it in
    // place and the held list is state, not this view of it.
    const filteredDetails = computed(() => {
        let filtered = [...details.value];

        // Apply search filter
        if (searchQuery.value) {
            filtered = filtered.filter((company) => 
                company.name.toLowerCase().includes(searchQuery.value.toLowerCase())
            );
        }

        // Apply type filter
        if (selectedType.value && selectedType.value !== 'all') {
            filtered = filtered.filter((company) => company.type.value === selectedType.value);
        }

        // Apply status filter
        if (selectedStatus.value && selectedStatus.value !== 'all') {
            filtered = filtered.filter((company) => {
                const statusLabel = getExposureStatusLabel(company.status);
                const selectedStatusLower = selectedStatus.value.toLowerCase();
                
                // If "exposed" is selected, include Exposed and Failed statuses (but NOT in_progress)
                if (selectedStatusLower === 'exposed') {
                    return (statusLabel.toLowerCase() === 'exposed' || statusLabel.toLowerCase() === 'failed') &&
                           company.status !== 'in_progress';
                }
                
                // If "in progress" is selected, include only IN_PROGRESS status
                if (selectedStatusLower === 'in progress') {
                    return company.status === 'in_progress';
                }
                
                // If "closed" is selected, include only CLOSED status
                if (selectedStatusLower === 'closed') {
                    return statusLabel.toLowerCase() === 'closed';
                }
                
                // If "clean" is selected, include only CLEAN status  
                if (selectedStatusLower === 'clean') {
                    return statusLabel.toLowerCase() === 'clean';
                }
                
                return statusLabel.toLowerCase() === selectedStatusLower;
            });
        }

        // Apply sorting
        const statusOrder = (status: ExposureStatus) => {
            // Get the display label for the status and define sort order
            const statusLabel = getExposureStatusLabel(status);
            const orderMap: Record<string, number> = {
                'Clean': 0,
                'Closed': 1,
                'Failed': 2,
                'Exposed': 3
            };
            return orderMap[statusLabel] ?? 4; // Unknown statuses go last
        };

        // Helper function to convert date string to timestamp (used in multiple sort cases)
        const getTimeOrMin = (val: string | undefined) => {
            if (!val || val === 'N/A') return Number.MIN_SAFE_INTEGER;
            const t = new Date(val).getTime();
            return isNaN(t) ? Number.MIN_SAFE_INTEGER : t;
        };

        filtered.sort((a, b) => {
            let result = 0;
            switch (sortKey.value) {
                case 'priority':
                    // Default sort order:
                    // 1. people_search exposed (0)
                    // 2. all other exposed (1)
                    // 3. people_search in_progress (2)
                    // 4. all other in_progress (3)
                    // 5. closed/failed (4)
                    // 6. people_search not_found (5)
                    // 7. all other not_found (6)
                    // 8. everything else (7)
                    const getPriorityScore = (company: CompanyExposure) => {
                        const status = company.status;
                        const companyType = company.type?.value || '';
                        const isPeopleSearchType = ['people_search', 'people_search_affiliate'].includes(companyType);
                        
                        // 1. people_search exposed first
                        if (isPeopleSearchType && (status === 'exposed' || status === 'pending')) {
                            return 0;
                        }
                        
                        // 2. all other exposed
                        if (!isPeopleSearchType && (status === 'exposed' || status === 'pending')) {
                            return 1;
                        }
                        
                        // 3. people_search in_progress
                        if (isPeopleSearchType && status === 'in_progress') {
                            return 2;
                        }
                        
                        // 4. all other in_progress
                        if (!isPeopleSearchType && status === 'in_progress') {
                            return 3;
                        }
                        
                        // 5. closed/failed
                        if (status === 'closed' || status === 'failed') {
                            return 4;
                        }
                        
                        // 6. people_search not_found
                        if (isPeopleSearchType && status === 'not_found') {
                            return 5;
                        }
                        
                        // 7. all other not_found
                        if (!isPeopleSearchType && status === 'not_found') {
                            return 6;
                        }
                        
                        // 8. everything else
                        return 7;
                    };
                    
                    const priorityA = getPriorityScore(a);
                    const priorityB = getPriorityScore(b);
                    
                    // Primary sort by priority score (lower number = higher priority)
                    if (priorityA !== priorityB) {
                        return priorityA - priorityB;
                    }
                    
                    // Secondary sort by last action descending (most recent first) for same priority items
                    const timeA = getTimeOrMin(a.last_action);
                    const timeB = getTimeOrMin(b.last_action);
                    return timeB - timeA; // Descending order (most recent first)
                case 'company':
                    result = (a.name || '').localeCompare(b.name || '');
                    break;
                case 'lastAction':
                    // Handle 'N/A' as the oldest date
                    result = getTimeOrMin(a.last_action) - getTimeOrMin(b.last_action);
                    break;
                case 'events':
                    result = (a.events ?? 0) - (b.events ?? 0);
                    break;
                case 'active':
                    result = (a.active ?? 0) - (b.active ?? 0);
                    break;
                case 'inProgress':
                    result = (a.in_progress ?? 0) - (b.in_progress ?? 0);
                    break;
                case 'closed':
                    // The Closed column states each company's completed removals.
                    result = (a.completed ?? 0) - (b.completed ?? 0);
                    break;
                case 'status':
                    result = statusOrder(a.status) - statusOrder(b.status);
                    break;
            }
            return sortOrder.value === 'desc' ? -result : result;
        });

        return filtered;
    });

    // The Details list's pages, and the one on screen.
    const detailsPageCount = computed(() => Math.max(1, Math.ceil(filteredDetails.value.length / DETAILS_PAGE_SIZE)));
    const paginatedDetails = computed(() => {
        const startIndex = (currentPage.value - 1) * DETAILS_PAGE_SIZE;
        return filteredDetails.value.slice(startIndex, startIndex + DETAILS_PAGE_SIZE);
    });

    // A read can bring fewer companies than the page the member was on (a retry, a refresh), and the page follows the
    // list back rather than showing an empty page, so an empty list only ever means one of the causes `emptyCause`
    // names. Data Brokers keeps its own pages the same way; a page it has is always one Details has.
    watch(detailsPageCount, (last) => {
        if (currentPage.value > last) currentPage.value = last;
    });

    // A Type or Status filter is narrowing the list. The dropdowns treat both '' and 'all' as unset.
    const hasFiltersActive = computed(
        () =>
            (!!selectedType.value && selectedType.value !== 'all') ||
            (!!selectedStatus.value && selectedStatus.value !== 'all')
    );

    // Why the list has nothing to show, or null while it has companies to show. With no rows held, the read found no
    // companies, and no search or filter changes that. With rows held, a filter or the search is what left the list
    // empty (only they narrow it), and the reader can act only on the one they are in: a filtered miss offers to clear
    // the filters, a search miss offers to have the company added. Both views of the list read this one answer.
    const emptyCause = computed<ExposureEmptyCause | null>(() => {
        if (filteredDetails.value.length > 0) return null;
        if (details.value.length === 0) return 'nothing-found';
        return hasFiltersActive.value ? 'filters' : 'search';
    });

    // The exposures list has nothing to show AND nothing is on its way: no rows,
    // no external hits, no search in flight, no error to report, and the first
    // load has actually finished. Anything less and the surface would flash
    // "Company not found" at a member whose data is still arriving.
    //
    // This lives on the store rather than on either table because BOTH render
    // it — the desktop table and the mobile list are two views of one result —
    // and when each kept its own copy the two drifted, which is how one of them
    // shipped an empty state the other had already replaced.
    const shouldShowEmptyState = computed(
        () =>
            paginatedDetails.value.length === 0 &&
            externalSearchResults.value.length === 0 &&
            !isSearchingExternal.value &&
            !externalSearchError.value &&
            !isFetching.value &&
            hasLoaded.value
    );

    // Whether a search has left the broker table sparse enough to be worth showing the member
    // the companies we track outside their monitored list.
    const canShowOtherCompanies = computed(() =>
        shouldShowOtherCompanies({
            searchQuery: searchQuery.value,
            selectedType: selectedType.value,
            selectedStatus: selectedStatus.value,
            brokerMatchCount: filteredDetails.value.length,
        })
    );

    // The cards the grid actually renders.
    const otherCompanies = computed(() => {
        if (!canShowOtherCompanies.value) return [];

        return selectOtherCompanies({
            results: externalSearchResults.value,
            monitoredCompanyIds: details.value.map((company) => company.id),
        });
    });

    // Metric aggregations shared by the details and data-brokers pages
    const isPeopleSearchCompany = (company: CompanyExposure) => {
        const type = company.type?.value || company.type;
        return type === 'people_search' || type === 'people_search_affiliate';
    };

    // People-search companies with status pending or in_progress
    const confirmedCount = computed(() => {
        return details.value.filter((company) =>
            isPeopleSearchCompany(company) &&
            (company.status === 'pending' || company.status === 'in_progress')
        ).length;
    });

    // Non-people-search companies with status pending or in_progress
    const likelyCount = computed(() => {
        return details.value.filter((company) =>
            !isPeopleSearchCompany(company) &&
            (company.status === 'pending' || company.status === 'in_progress')
        ).length;
    });

    // Total number of companies being monitored
    const companiesEngaged = computed(() => details.value.length);

    // Total events across all companies with status exposed or in_progress
    const exposuresDetected = computed(() => {
        return details.value
            .filter((company) => company.status === 'in_progress' || company.status === 'exposed')
            .reduce((sum, company) => sum + (company.events || 1), 0);
    });

    // A page calls this as a visit starts, before its read. The details count as unloaded until that read settles, so the
    // visit waits on its own read, as a first visit does, rather than showing an earlier visit's outcome. The removal
    // card's unavailable state depends on it: a failure an earlier visit left stays off the card while this read is out.
    // The list starts on its first page: the pager is shared by two pages that page by different sizes, so the page an
    // earlier visit left can be past the end of this one's list.
    function startVisit(): void {
        hasLoaded.value = false;
        currentPage.value = 1;
    }

    // The company rows and the removal figures, from one read.
    async function fetchDetails(): Promise<void> {
        // Reset expanded state to ensure all subtables start collapsed
        expandedCompanyName.value = null;

        if (!personal?.person) {
            fetchError.value = 'User not found';
            removalActivityError.value = fetchError.value;
            return;
        }

        const personId = personal.person.id;
        const generation = ++detailsGeneration;
        pendingDetailsFetch.value = { generation, personId };

        // Only the newest fetch for the person on screen may write state: a later fetch overtakes it,
        // and a person switch disowns it. Both are checked here rather than by the switch watcher
        // bumping the generation, so the outcome does not turn on whether that watcher runs before or
        // after the caller's fetch for the new person.
        const isCurrent = () => generation === detailsGeneration && personal.person?.id === personId;

        try {
            const answer = await exposuresService.getExposureDetails(personId);
            if (!isCurrent()) return;

            const arrivedAt = Date.now();
            details.value = answer.companies;
            detailsUpdatedAt.value = arrivedAt;
            fetchError.value = null;
            if (answer.removalActivity) {
                removalActivity.value = answer.removalActivity;
                removalActivityUpdatedAt.value = arrivedAt;
                removalActivityError.value = null;
            } else {
                // Figures already held are this person's, so they stay on screen, marked out of date by this failure.
                removalActivityError.value = 'The exposure detail came back without its removal figures';
            }
            nextTick(() => {
                if (isCurrent()) hasLoaded.value = true;
            });
        } catch (error: unknown) {
            if (!isCurrent()) return;

            fetchError.value = (error instanceof Error && error.message) || 'Failed to fetch exposure details';
            removalActivityError.value = fetchError.value;
            hasLoaded.value = true; // Still mark as loaded even if there's an error
        } finally {
            if (pendingDetailsFetch.value?.generation === generation) pendingDetailsFetch.value = null;
        }
    }

    async function fetchExposures(limit: number = 50) {
        if (!personal?.person) {
            return;
        }

        exposuresError.value = null;

        try {
            exposures.value = await exposuresService.getExposures(personal.person.id, limit);
        } catch (error: unknown) {
            exposuresError.value = (error instanceof Error && error.message) || 'Failed to fetch exposures';
        }
    }

    /**
     * The company's exposures, fetched once. A second caller during the flight joins the first
     * request rather than starting its own, and a resolved list is handed straight back.
     *
     * A failure caches nothing: an empty list is the claim "this company has no exposures", which
     * is not what a failed lookup learned. The error is recorded per company and the next call
     * asks again — joining prevents a loop while a request is open, and a caller that fires on
     * every render is that caller's bug, not a reason to serve it a wrong answer forever.
     */
    async function loadForCompany(companyId: number): Promise<Exposure[]> {
        if (!personal?.person) return [];

        const cached = exposuresByCompany.value[companyId];
        if (cached) return cached;

        const inFlight = inFlightByCompany.get(companyId);
        if (inFlight) return inFlight;

        const personId = personal.person.id;
        // Named before it is built so the body can ask whether the map still holds it.
        let request!: Promise<Exposure[]>;
        request = (async () => {
            loadingCompanyIds.value[companyId] = true;
            delete companyErrors.value[companyId];
            try {
                const data = await exposuresService.getExposuresForCompany<Exposure>(personId, companyId);
                // The person switched under this request; its list belongs to someone else now.
                if (personal.person?.id !== personId) return [];

                exposuresByCompany.value[companyId] = data;
                return data;
            } catch (error: unknown) {
                const message = (error instanceof Error && error.message) || 'Failed to fetch company exposures';
                exposuresError.value = message;
                companyErrors.value[companyId] = message;
                return [];
            } finally {
                // A person switch clears the cache and starts a fresh request for the same company;
                // this one settling afterwards must not delete that request or stop its spinner.
                if (inFlightByCompany.get(companyId) === request) {
                    inFlightByCompany.delete(companyId);
                    loadingCompanyIds.value[companyId] = false;
                }
            }
        })();

        inFlightByCompany.set(companyId, request);
        return request;
    }

    const isLoadingCompanyExposures = (companyId: number): boolean => loadingCompanyIds.value[companyId] === true;

    const companyExposures = (companyId: number): Exposure[] => exposuresByCompany.value[companyId] ?? [];

    /** Why this company's list is empty, when it is empty because the lookup failed. */
    const companyExposuresError = (companyId: number): string | undefined => companyErrors.value[companyId];

    /** Whether this company's list has been fetched — an empty list is an answer, a missing one is not. */
    const hasLoadedCompanyExposures = (companyId: number): boolean => exposuresByCompany.value[companyId] !== undefined;

    function invalidateCompanyExposures() {
        exposuresByCompany.value = {};
        loadingCompanyIds.value = {};
        companyErrors.value = {};
        inFlightByCompany.clear();
    }

    async function searchCompanies(name: string) {
        searchQuery.value = name;
        currentPage.value = 1;

        // Clear previous external search results
        invalidateExternalSearch();

        // Wait for the next tick to ensure filteredDetails is updated
        await nextTick();

        // Only spend an API call when the grid could actually render the results
        if (canShowOtherCompanies.value) {
            armExternalSearch(name);
        } else {
            debouncedExternalSearch.cancel();
        }
    }

    /**
     * Mark the lookup as running the moment it is queued, not when the request finally leaves.
     * The 500ms debounce is otherwise a window where results are empty and nothing is "loading",
     * which reads as "no such company" and invites a duplicate request seconds before the real
     * match arrives.
     */
    function armExternalSearch(query: string) {
        isSearchingExternal.value = true;
        debouncedExternalSearch(query);
    }

    /**
     * Drop the current results and disown any request still in flight. Debouncing alone doesn't
     * cover this: once a request has left, a second query 500ms later races it, and the slower
     * response wins whichever order they land in.
     */
    function invalidateExternalSearch() {
        externalSearchGeneration++;
        externalSearchResults.value = [];
        externalSearchError.value = null;
        isSearchingExternal.value = false;
    }

    async function searchExternalCompanies(query: string) {
        if (!query || query.length < OTHER_COMPANIES_MIN_QUERY_LENGTH) return;

        const generation = ++externalSearchGeneration;

        // A failure already on record stays until this lookup answers: only asking again for the same query reaches here
        // with one, since a new query or filter clears it first, and the failure is where the member asked again from.
        isSearchingExternal.value = true;

        try {
            // Filter out companies that already exist in the details store
            const existingCompanyIds = details.value.map(company => company.id);
            const collected: PublicCompany[] = [];
            let page = 1;
            let lastPage = 1;

            // A page can be entirely monitored companies or excluded types while a usable match
            // waits on the next one. Keep walking until the grid could be filled, the results run
            // out, or we hit the page ceiling — otherwise we would claim nothing matched.
            do {
                const response = await companiesService.searchCompanies(
                    query,
                    OTHER_COMPANIES_PAGE_SIZE,
                    page,
                    // The grid only shows with no Type filter applied, so never narrow the search.
                    ''
                );

                // A newer query overtook this one — its results are the ones on screen now.
                if (generation !== externalSearchGeneration) return;

                collected.push(...((response?.data || []) as unknown as PublicCompany[]));
                lastPage = response?.meta?.last_page ?? page;
                page++;
            } while (
                page <= lastPage &&
                page <= OTHER_COMPANIES_MAX_PAGES &&
                selectOtherCompanies({
                    results: collected,
                    monitoredCompanyIds: existingCompanyIds,
                }).length < OTHER_COMPANIES_LIMIT
            );

            externalSearchResults.value = collected.filter(
                (company) => !existingCompanyIds.includes(company.id)
            );
            externalSearchError.value = null;
        } catch (error: unknown) {
            if (generation !== externalSearchGeneration) return;

            externalSearchError.value = (error instanceof Error && error.message) || 'Failed to search external companies';
            console.error('External search failed:', error);
        } finally {
            // Leave the flag alone if a newer request owns it, or the stale one would clear
            // the spinner while the current search is still running.
            if (generation === externalSearchGeneration) {
                isSearchingExternal.value = false;
            }
        }
    }

    function clearSearch() {
        searchQuery.value = '';
        currentPage.value = 1;
        invalidateExternalSearch();
        // Cancel any pending debounced search
        debouncedExternalSearch.cancel();
    }

    // Helper function to reset all filters
    function resetFilters() {
        isResetting.value = true;
        searchQuery.value = '';
        selectedType.value = '';
        selectedStatus.value = '';
        currentPage.value = 1;
        invalidateExternalSearch();
        // Cancel any pending debounced search
        debouncedExternalSearch.cancel();

        nextTick(() => {
            isResetting.value = false;
        });
    }

    /**
     * Re-decide whether the other-companies grid belongs on screen after a filter changes.
     * Applying a Type or Status filter drops the results; clearing it back to "All" fetches
     * them again, so the grid returns instead of staying stale-empty.
     */
    async function syncOtherCompanies() {
        invalidateExternalSearch();

        await nextTick();

        if (canShowOtherCompanies.value) {
            armExternalSearch(searchQuery.value);
        } else {
            debouncedExternalSearch.cancel();
        }
    }

    function filterByType(type: string) {
        currentPage.value = 1;
        selectedType.value = type;
        syncOtherCompanies();
    }

    function filterByStatus(status: string) {
        currentPage.value = 1;
        selectedStatus.value = status;
        syncOtherCompanies();
    }

    function toggleExpanded(companyName: string) {
        expandedCompanyName.value = expandedCompanyName.value === companyName ? null : companyName;
    }

    function setSort(key: typeof sortKey.value) {
        if (sortKey.value === key) {
            sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc';
        } else {
            sortKey.value = key;
            sortOrder.value = 'desc';
        }
    }

    // One member's rows, broker lists, removal figures, failed reads and place in the list say nothing about the next
    // one's, and a page that stays mounted across the switch must not show them as the new member's. The details go
    // unloaded, so a page waits on the new member's read rather than rendering what it no longer holds, and their list
    // starts on its first page. Loading the new member's details is the caller's job.
    watch(() => personal.person?.id, () => {
        invalidateCompanyExposures();
        details.value = [];
        detailsUpdatedAt.value = null;
        hasLoaded.value = false;
        currentPage.value = 1;
        removalActivity.value = null;
        removalActivityUpdatedAt.value = null;
        fetchError.value = null;
        removalActivityError.value = null;
    });

    // Watch for search query changes to trigger external search
    watch(searchQuery, async (newQuery) => {
        await searchCompanies(newQuery);
    });

    const getSeverityBadge = (severity: string = ''): string => {
        const severityLower = severity?.toLowerCase() || 'clean';
        if (severityLower === 'clean') return ''; 
        if (severityLower === 'failed') return 'error';
        if (severityLower === 'exposed') return 'warning';
        if (severityLower === 'closed') return 'success';
        return 'success';
    };

    return {
        isFetching,
        fetchError,
        hasLoaded,
        loadFailed,
        detailsStaleSince,
        figuresNote,
        startVisit,
        fetchDetails,
        fetchExposures,
        details,
        detailsUpdatedAt,
        removalActivity,
        removalActivityError,
        removalActivityUpdatedAt,
        exposures,
        exposuresError,
        loadForCompany,
        companyExposures,
        companyExposuresError,
        hasLoadedCompanyExposures,
        isLoadingCompanyExposures,
        invalidateCompanyExposures,
        filteredDetails,
        detailsPageCount,
        paginatedDetails,
        confirmedCount,
        likelyCount,
        companiesEngaged,
        exposuresDetected,
        searchCompanies,
        filterByType,
        filterByStatus,
        currentPage,
        expandedCompanyName,
        toggleExpanded,
        clearSearch,
        searchQuery,
        selectedType,
        selectedStatus,
        hasFiltersActive,
        emptyCause,
        shouldShowEmptyState,
        resetFilters,
        isResetting,
        getSeverityBadge,
        // Sorting
        sortKey,
        sortOrder,
        setSort,
        // External search
        externalSearchResults,
        isSearchingExternal,
        externalSearchError,
        searchExternalCompanies,
        // Companies outside the monitored broker list
        canShowOtherCompanies,
        otherCompanies,
    };
}, {
    // The member's filters survive a reload of the page they narrowed.
    persist: {
        pick: ['selectedType', 'selectedStatus', 'searchQuery'],
    },
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useExposuresStore, import.meta.hot))
}